import { useState } from 'react';

interface Props {
  onClose: () => void;
  onGenerate: (data: CustomPlanData) => void;
}

export interface CustomPlanData {
  businessName: string;
  industry: string;
  stage: string;
  goals: string[];
  targetMarket: string;
  revenue: string;
  teamSize: string;
  fundingNeeded: string;
  uniqueValue: string;
}

const INDUSTRIES = [
  'Technology / SaaS', 'E-commerce / Retail', 'Restaurant / Food & Beverage',
  'Healthcare / Medical', 'Real Estate', 'Finance / FinTech', 'Education / EdTech',
  'Manufacturing', 'Consulting / Services', 'Hospitality / Travel',
  'Creative / Media', 'Agriculture', 'Logistics / Supply Chain',
  'Fitness & Wellness', 'Fashion & Beauty', 'Non-Profit / Social Enterprise', 'Other',
];

const STAGES = [
  { value: 'idea', label: 'Idea Stage', icon: 'ri-lightbulb-line', desc: 'Just getting started' },
  { value: 'pre-revenue', label: 'Pre-Revenue', icon: 'ri-seedling-line', desc: 'Building the product' },
  { value: 'early', label: 'Early Revenue', icon: 'ri-bar-chart-line', desc: 'First customers' },
  { value: 'growth', label: 'Growth Stage', icon: 'ri-rocket-line', desc: 'Scaling up' },
  { value: 'established', label: 'Established', icon: 'ri-building-line', desc: 'Mature business' },
];

const GOALS = [
  'Raise Funding', 'Bank Loan', 'Internal Strategy', 'Find Co-Founders',
  'Government Grant', 'Partnership', 'Franchise', 'Acquisition',
];

const STEPS = ['Business Info', 'Goals & Market', 'Financials', 'Review'];

const OUTLINE_SECTIONS = [
  { icon: 'ri-file-text-line', title: 'Executive Summary', desc: 'High-level overview of your business opportunity' },
  { icon: 'ri-search-eye-line', title: 'Market Analysis', desc: 'Industry size, trends, and competitive landscape' },
  { icon: 'ri-user-heart-line', title: 'Target Customer', desc: 'Ideal customer profile and buyer personas' },
  { icon: 'ri-lightbulb-flash-line', title: 'Product / Service', desc: 'What you offer and your unique value proposition' },
  { icon: 'ri-megaphone-line', title: 'Marketing Strategy', desc: 'Go-to-market plan and customer acquisition' },
  { icon: 'ri-settings-3-line', title: 'Operations Plan', desc: 'Day-to-day operations and processes' },
  { icon: 'ri-team-line', title: 'Management Team', desc: 'Key team members and organizational structure' },
  { icon: 'ri-line-chart-line', title: 'Financial Projections', desc: '3-year revenue, expenses, and cash flow' },
  { icon: 'ri-funds-line', title: 'Funding Requirements', desc: 'Capital needed and how it will be used' },
  { icon: 'ri-road-map-line', title: 'Milestones & Roadmap', desc: 'Key goals and timeline for execution' },
];

export default function GenerateCustomPlanModal({ onClose, onGenerate }: Props) {
  const [step, setStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [form, setForm] = useState<CustomPlanData>({
    businessName: '',
    industry: '',
    stage: '',
    goals: [],
    targetMarket: '',
    revenue: '',
    teamSize: '',
    fundingNeeded: '',
    uniqueValue: '',
  });

  const update = (key: keyof CustomPlanData, value: string | string[]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const toggleGoal = (goal: string) => {
    setForm(prev => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal],
    }));
  };

  const canNext = () => {
    if (step === 0) return form.businessName.trim() && form.industry && form.stage;
    if (step === 1) return form.goals.length > 0 && form.targetMarket.trim();
    if (step === 2) return form.revenue && form.teamSize;
    return true;
  };

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      setGenerated(true);
    }, 2800);
  };

  const progressPct = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <i className="ri-magic-line text-white text-xl"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Generate Custom Plan</h2>
                <p className="text-white/70 text-xs">AI drafts your personalized business plan outline</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          {/* Progress */}
          {!generated && (
            <>
              <div className="flex items-center gap-2 mb-3">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex items-center gap-2 flex-1">
                    <div className={`flex items-center gap-1.5 ${i <= step ? 'text-white' : 'text-white/40'}`}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${i < step ? 'bg-white text-violet-600 border-white' : i === step ? 'bg-white/20 border-white' : 'bg-transparent border-white/30'}`}>
                        {i < step ? <i className="ri-check-line text-xs"></i> : i + 1}
                      </div>
                      <span className="text-xs font-medium hidden sm:block">{s}</span>
                    </div>
                    {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 rounded-full ${i < step ? 'bg-white' : 'bg-white/25'}`} />}
                  </div>
                ))}
              </div>
              <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
              </div>
            </>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-8">
          {generating ? (
            <div className="flex flex-col items-center justify-center py-16 gap-6">
              <div className="relative w-20 h-20">
                <div className="absolute inset-0 rounded-full border-4 border-violet-100"></div>
                <div className="absolute inset-0 rounded-full border-4 border-violet-600 border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-ai-generate text-violet-600 text-2xl"></i>
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-bold text-slate-900 mb-2">AI is drafting your plan…</h3>
                <p className="text-sm text-slate-500">Analyzing your business details and generating a custom outline</p>
              </div>
              <div className="w-full max-w-xs space-y-2">
                {['Analyzing industry trends', 'Mapping competitive landscape', 'Structuring financial model', 'Generating outline sections'].map((label, i) => (
                  <div key={label} className="flex items-center gap-3 text-sm text-slate-600">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <div className="w-3 h-3 rounded-full bg-violet-200 animate-pulse" style={{ animationDelay: `${i * 0.3}s` }}></div>
                    </div>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          ) : generated ? (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <i className="ri-check-double-line text-emerald-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Your Plan Outline is Ready!</h3>
                  <p className="text-xs text-slate-500">AI-generated for <strong>{form.businessName}</strong> · {form.industry}</p>
                </div>
              </div>
              <div className="space-y-3 mb-6">
                {OUTLINE_SECTIONS.map((sec, i) => (
                  <div key={sec.title} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-violet-200 transition-colors group">
                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-violet-200 transition-colors">
                      <i className={`${sec.icon} text-violet-600 text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-400">0{i + 1}</span>
                        <h4 className="text-sm font-semibold text-slate-800">{sec.title}</h4>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{sec.desc}</p>
                    </div>
                    <i className="ri-check-line text-emerald-500 text-sm flex-shrink-0 mt-0.5"></i>
                  </div>
                ))}
              </div>
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 flex items-start gap-3">
                <i className="ri-information-line text-violet-500 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-xs text-violet-700 leading-relaxed">
                  Sign in to unlock the full AI-generated plan with detailed content, financial projections, and export options.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Step 0 */}
              {step === 0 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Business Name <span className="text-rose-500">*</span></label>
                    <input
                      type="text"
                      placeholder="e.g. Acme Corp, GreenLeaf Foods…"
                      value={form.businessName}
                      onChange={e => update('businessName', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent placeholder-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Industry <span className="text-rose-500">*</span></label>
                    <select
                      value={form.industry}
                      onChange={e => update('industry', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white text-slate-800 cursor-pointer"
                    >
                      <option value="">Select your industry…</option>
                      {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-3">Business Stage <span className="text-rose-500">*</span></label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {STAGES.map(s => (
                        <button
                          key={s.value}
                          onClick={() => update('stage', s.value)}
                          className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${form.stage === s.value ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-violet-300'}`}
                        >
                          <div className="w-7 h-7 flex items-center justify-center mb-2">
                            <i className={`${s.icon} text-lg ${form.stage === s.value ? 'text-violet-600' : 'text-slate-400'}`}></i>
                          </div>
                          <div className={`text-xs font-bold ${form.stage === s.value ? 'text-violet-700' : 'text-slate-700'}`}>{s.label}</div>
                          <div className="text-xs text-slate-400 mt-0.5">{s.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Unique Value Proposition</label>
                    <textarea
                      placeholder="What makes your business different? What problem do you solve?"
                      value={form.uniqueValue}
                      onChange={e => update('uniqueValue', e.target.value)}
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 placeholder-slate-400 resize-none"
                    />
                    <p className="text-xs text-slate-400 mt-1 text-right">{form.uniqueValue.length}/500</p>
                  </div>
                </div>
              )}

              {/* Step 1 */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-3">Plan Goals <span className="text-rose-500">*</span> <span className="text-slate-400 font-normal">(select all that apply)</span></label>
                    <div className="flex flex-wrap gap-2">
                      {GOALS.map(goal => (
                        <button
                          key={goal}
                          onClick={() => toggleGoal(goal)}
                          className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all cursor-pointer whitespace-nowrap ${form.goals.includes(goal) ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:border-violet-300'}`}
                        >
                          {form.goals.includes(goal) && <i className="ri-check-line mr-1.5 text-xs"></i>}
                          {goal}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Target Market <span className="text-rose-500">*</span></label>
                    <textarea
                      placeholder="Describe your ideal customers — who are they, where are they, what do they need?"
                      value={form.targetMarket}
                      onChange={e => update('targetMarket', e.target.value)}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 placeholder-slate-400 resize-none"
                    />
                    <p className="text-xs text-slate-400 mt-1 text-right">{form.targetMarket.length}/500</p>
                  </div>
                </div>
              )}

              {/* Step 2 */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">Expected Annual Revenue <span className="text-rose-500">*</span></label>
                      <select
                        value={form.revenue}
                        onChange={e => update('revenue', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white text-slate-800 cursor-pointer"
                      >
                        <option value="">Select range…</option>
                        <option>Under $50K</option>
                        <option>$50K – $250K</option>
                        <option>$250K – $1M</option>
                        <option>$1M – $5M</option>
                        <option>$5M – $20M</option>
                        <option>$20M+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-800 mb-2">Team Size <span className="text-rose-500">*</span></label>
                      <select
                        value={form.teamSize}
                        onChange={e => update('teamSize', e.target.value)}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white text-slate-800 cursor-pointer"
                      >
                        <option value="">Select size…</option>
                        <option>Solo founder</option>
                        <option>2–5 people</option>
                        <option>6–15 people</option>
                        <option>16–50 people</option>
                        <option>50+ people</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-800 mb-2">Funding Needed</label>
                    <select
                      value={form.fundingNeeded}
                      onChange={e => update('fundingNeeded', e.target.value)}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white text-slate-800 cursor-pointer"
                    >
                      <option value="">Not seeking funding</option>
                      <option>Under $50K</option>
                      <option>$50K – $250K</option>
                      <option>$250K – $1M</option>
                      <option>$1M – $5M</option>
                      <option>$5M – $20M</option>
                      <option>$20M+</option>
                    </select>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <i className="ri-sparkling-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <p className="text-xs text-amber-700 leading-relaxed">
                      Our AI will use these details to generate a tailored financial model with realistic projections for your industry and stage.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3 — Review */}
              {step === 3 && (
                <div className="space-y-5">
                  <p className="text-sm text-slate-500">Review your details before AI generates your plan outline.</p>
                  <div className="bg-slate-50 rounded-2xl border border-slate-200 divide-y divide-slate-200 overflow-hidden">
                    {[
                      { label: 'Business Name', value: form.businessName },
                      { label: 'Industry', value: form.industry },
                      { label: 'Stage', value: STAGES.find(s => s.value === form.stage)?.label || '' },
                      { label: 'Goals', value: form.goals.join(', ') || '—' },
                      { label: 'Target Market', value: form.targetMarket || '—' },
                      { label: 'Expected Revenue', value: form.revenue || '—' },
                      { label: 'Team Size', value: form.teamSize || '—' },
                      { label: 'Funding Needed', value: form.fundingNeeded || 'Not seeking funding' },
                    ].map(row => (
                      <div key={row.label} className="flex items-start gap-4 px-5 py-3">
                        <span className="text-xs font-semibold text-slate-400 w-36 flex-shrink-0 pt-0.5">{row.label}</span>
                        <span className="text-sm text-slate-800 font-medium">{row.value}</span>
                      </div>
                    ))}
                  </div>
                  {form.uniqueValue && (
                    <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
                      <p className="text-xs font-semibold text-violet-600 mb-1">Unique Value Proposition</p>
                      <p className="text-sm text-slate-700">{form.uniqueValue}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-slate-100 flex items-center justify-between flex-shrink-0 bg-white">
          {generated ? (
            <>
              <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
                Close
              </button>
              <button
                onClick={() => onGenerate(form)}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-edit-line"></i>
                Start Writing Full Plan
              </button>
            </>
          ) : generating ? (
            <div className="w-full flex justify-center">
              <span className="text-sm text-slate-400">Please wait…</span>
            </div>
          ) : (
            <>
              <button
                onClick={() => step === 0 ? onClose() : setStep(s => s - 1)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                {step === 0 ? 'Cancel' : '← Back'}
              </button>
              {step < 3 ? (
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canNext()}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap"
                >
                  Continue →
                </button>
              ) : (
                <button
                  onClick={handleGenerate}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-magic-line"></i>
                  Generate My Plan
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
